// src/pages/client/DashboardPage.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isFuture, parseISO, isToday, isPast } from 'date-fns';
import { useAppSelector } from '../../hooks/useRedux';
import { Appointment } from '../../types/appointment';
import { ClientStats } from '../../types/auth';
import { Stylist } from '../../types/stylist';
import { formatCurrency } from '../../utils/formatters';
import appointmentService from '../../services/appointmentService';
import realtimeService from '../../services/realtimeService';
import { patchAppointmentList, AppointmentUpdateEvent } from '../../utils/appointmentRealtime';
import { useDocumentVisibleRefetch } from '../../hooks/useDocumentVisibleRefetch';
import stylistService from '../../services/stylistService';
import { getNotifications } from '../../services/notificationService';
import { CalendarIcon, CheckIcon, HeartIcon, StarIcon, SearchIcon, PersonIcon, ArrowRightIcon } from '../../components/common/Icons';
import StylistCard from '../../components/client/StylistCard';
import DiscountBadge from '../../components/common/DiscountBadge';
import { publicAppointmentRef, publicStylistRef } from '../../utils/publicRefs';
import { getWelcomeDisplayName } from '../../utils/welcomeDisplayName';

// Helper: appointment counts + loyalty; favorites come from /favorites (not derived from history).
const calculateClientStats = (
  appointments: Appointment[],
  user: any,
  favoriteStylistIds: string[]
): ClientStats => {
  const completed = appointments.filter(apt => apt.status === 'completed');
  const upcoming = appointments.filter(apt => {
    const aptDate = parseISO(apt.dateTime);
    return isFuture(aptDate) && ['pending', 'confirmed'].includes(apt.status);
  });

  const lastAppointment = appointments.length > 0
    ? appointments
        .filter(apt => apt.status === 'completed')
        .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())[0]
    : null;

  return {
    totalAppointments: appointments.length,
    completedAppointments: completed.length,
    upcomingAppointments: upcoming.length,
    totalSpent: 0,
    loyaltyPoints: user?.loyaltyPoints || 0,
    favoriteStylists: favoriteStylistIds,
    lastAppointment: lastAppointment ? lastAppointment.dateTime : undefined,
  };
};

const ClientDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  
  const [isLoading, setIsLoading] = useState(true);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [pastAppointments, setPastAppointments] = useState<Appointment[]>([]);
  const [clientStats, setClientStats] = useState<ClientStats | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [nearbyStylists, setNearbyStylists] = useState<Stylist[]>([]);
  const [nearbyStylistsLoading, setNearbyStylistsLoading] = useState(false);
  const nearbyFetchCancelledRef = useRef(false);
  const [favoritesError, setFavoritesError] = useState<string | null>(null);

  const hasClientLocation = Boolean(user?.location?.city && user?.location?.state);

  const fetchNearbyStylists = useCallback(async () => {
    if (!hasClientLocation || !user?.location?.city || !user?.location?.state) return;
    setNearbyStylistsLoading(true);
    try {
      const params: Record<string, string | number | undefined> = {
        city: user.location.city,
        state: user.location.state,
        limit: 6,
      };
      const coords = (user.location as { coordinates?: { coordinates?: number[] } })?.coordinates?.coordinates;
      if (Array.isArray(coords) && coords.length >= 2) {
        params.longitude = coords[0];
        params.latitude = coords[1];
        params.maxDistance = 25; // miles for "nearby" on dashboard
      }
      const list = await stylistService.getStylists(params);
      if (nearbyFetchCancelledRef.current) return;
      setNearbyStylists((list || []).slice(0, 6));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Dashboard] Nearby stylists fetch failed:', err);
      }
      if (!nearbyFetchCancelledRef.current) setNearbyStylists([]);
    } finally {
      if (!nearbyFetchCancelledRef.current) setNearbyStylistsLoading(false);
    }
  }, [user?.location, hasClientLocation]);

  useEffect(() => {
    nearbyFetchCancelledRef.current = false;
    fetchNearbyStylists();
    return () => {
      nearbyFetchCancelledRef.current = true;
    };
  }, [fetchNearbyStylists]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Fetch all appointments
        const appointments = await appointmentService.getAppointments();
        
        // Filter upcoming and past appointments
        const upcoming = appointments
          .filter(apt => {
            const aptDate = parseISO(apt.dateTime);
            return isFuture(aptDate) && ['pending', 'confirmed'].includes(apt.status);
          })
          .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
        setUpcomingAppointments(upcoming);

        const past = appointments
          .filter(apt => {
            const aptDate = parseISO(apt.dateTime);
            return isPast(aptDate) || apt.status === 'completed';
          })
          .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())
          .slice(0, 3); // Show only 3 most recent
        setPastAppointments(past);

        let favoriteStylistIds: string[] = [];
        try {
          const favRes = await stylistService.getFavoriteStylists();
          favoriteStylistIds = (favRes.stylists || [])
            .map((s) => s._id || (s as { id?: string }).id)
            .filter((id): id is string => Boolean(id));
          setFavoritesError(null);
        } catch (favErr) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[Dashboard] Favorites fetch failed:', favErr);
          }
          setFavoritesError('Could not load saved stylists. Refresh the page to try again.');
        }

        const stats = calculateClientStats(appointments, user, favoriteStylistIds);
        setClientStats(stats);

        // Fetch recent notifications
        const notifsResponse = await getNotifications({ limit: 5 });
        setNotifications(notifsResponse.data);

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyClientAppointmentLists = useCallback((appointments: Appointment[]) => {
    const upcoming = appointments
      .filter((apt) => {
        const aptDate = parseISO(apt.dateTime);
        return isFuture(aptDate) && ['pending', 'confirmed'].includes(apt.status);
      })
      .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
    setUpcomingAppointments(upcoming);

    const past = appointments
      .filter((apt) => {
        const aptDate = parseISO(apt.dateTime);
        return isPast(aptDate) || apt.status === 'completed';
      })
      .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())
      .slice(0, 3);
    setPastAppointments(past);
    return { upcoming, past };
  }, []);

  const refreshClientAppointments = useCallback(async () => {
    const appointments = await appointmentService.getAppointments();
    applyClientAppointmentLists(appointments);
    setClientStats((prev) => calculateClientStats(
      appointments,
      user,
      prev?.favoriteStylists || []
    ));
  }, [applyClientAppointmentLists, user]);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleAppointmentRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      refreshClientAppointments().catch((error) => {
        console.error('[Dashboard] Appointment refresh failed:', error);
      });
    }, 400);
  }, [refreshClientAppointments]);

  useEffect(() => {
    return realtimeService.onAppointmentUpdate((data: AppointmentUpdateEvent) => {
      setUpcomingAppointments((prev) => patchAppointmentList(prev, data));
      setPastAppointments((prev) => patchAppointmentList(prev, data));
      scheduleAppointmentRefresh();
    });
  }, [scheduleAppointmentRefresh]);

  useDocumentVisibleRefetch(() => {
    scheduleAppointmentRefresh();
  });

  useEffect(() => () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  const formatAppointmentDate = (dateTime: string) => {
    try {
      if (!dateTime) return 'Date TBD';
      const appointmentDate = parseISO(dateTime);
      if (isNaN(appointmentDate.getTime())) {
        return dateTime || 'Date TBD';
      }
      if (isToday(appointmentDate)) {
        return 'Today';
      }
      return format(appointmentDate, 'EEE, MMM d, yyyy');
    } catch (error) {
      return dateTime || 'Date TBD';
    }
  };

  const formatAppointmentTime = (dateTime: string) => {
    try {
      if (!dateTime) return 'Time TBD';
      const appointmentTime = parseISO(dateTime);
      if (isNaN(appointmentTime.getTime())) {
        return dateTime || 'Time TBD';
      }
      return format(appointmentTime, 'h:mm a');
    } catch (error) {
      return dateTime || 'Time TBD';
    }
  };

  return (
    <div className="client-dashboard-modern">
      {/* Header Section */}
      <div className="dashboard-header-modern">
        <div className="dashboard-header-modern__content">
          <h1 className="dashboard-header-modern__title">
            {(() => {
              const displayName = getWelcomeDisplayName(user);
              return displayName ? `Hello, ${displayName}` : 'Hello';
            })()}
          </h1>
          <p className="dashboard-header-modern__subtitle">
            Here's what's happening with your appointments today
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid-modern">
        {isLoading ? (
          <>
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="stat-card-modern skeleton"></div>
            ))}
          </>
        ) : (
          <>
            <div className="stat-card-modern stat-card-modern--primary">
              <div className="stat-card-modern__icon-wrapper">
                <CalendarIcon />
              </div>
              <div className="stat-card-modern__content">
                <div className="stat-card-modern__value">{clientStats?.upcomingAppointments || upcomingAppointments.length || 0}</div>
                <div className="stat-card-modern__label">Upcoming</div>
              </div>
              <div className="stat-card-modern__accent"></div>
            </div>
            <div className="stat-card-modern stat-card-modern--success">
              <div className="stat-card-modern__icon-wrapper">
                <CheckIcon />
              </div>
              <div className="stat-card-modern__content">
                <div className="stat-card-modern__value">{clientStats?.completedAppointments || pastAppointments.length || 0}</div>
                <div className="stat-card-modern__label">Completed</div>
              </div>
              <div className="stat-card-modern__accent"></div>
            </div>
            <div
              className="stat-card-modern stat-card-modern--money"
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/client/browse')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/client/browse');
                }
              }}
              title={`${clientStats?.favoriteStylists?.length ?? 0} saved stylist(s). Open directory to find more.`}
            >
              <div className="stat-card-modern__icon-wrapper">
                <HeartIcon />
              </div>
              <div className="stat-card-modern__content">
                <div className="stat-card-modern__value">{clientStats?.favoriteStylists?.length ?? 0}</div>
                <div className="stat-card-modern__label">Saved stylists</div>
              </div>
              <div className="stat-card-modern__accent"></div>
            </div>
            <div className="stat-card-modern stat-card-modern--points">
              <div className="stat-card-modern__icon-wrapper">
                <StarIcon />
              </div>
              <div className="stat-card-modern__content">
                <div className="stat-card-modern__value">{clientStats?.loyaltyPoints || 0}</div>
                <div className="stat-card-modern__label">Loyalty Points</div>
              </div>
              <div className="stat-card-modern__accent"></div>
            </div>
          </>
        )}
      </div>

      {!isLoading && favoritesError && (
        <div className="client-dashboard-modern__favorites-alert" role="status">
          {favoritesError}
        </div>
      )}

      <div className="dashboard-content-modern">
        <div className="dashboard-content-modern__main">
        {/* Upcoming Appointments */}
        <div className="section-modern">
          <div className="section-modern__header">
            <h2 className="section-modern__title">Upcoming Appointments</h2>
            {upcomingAppointments.length > 0 && (
              <button
                className="section-modern__action"
                onClick={() => navigate('/client/appointments')}
              >
                View All
                <ArrowRightIcon />
              </button>
            )}
          </div>

          <div className="section-modern__content">
            {isLoading ? (
              <>
                <div className="appointment-card-modern skeleton"></div>
                <div className="appointment-card-modern skeleton"></div>
              </>
            ) : upcomingAppointments.length > 0 ? (
              upcomingAppointments.slice(0, 3).map((appointment) => {
                const servicePrice = appointment.service?.finalPrice ?? appointment.service?.originalPrice ?? 0;
                const addons = appointment.addons ?? [];
                const addonsTotal = addons.reduce(
                  (sum, addon) => sum + (typeof addon.priceCents === 'number' ? addon.priceCents / 100 : 0),
                  0
                );
                const totalPrice = servicePrice + addonsTotal;
                const hasAddons = addons.length > 0;
                return (
                  <div
                    key={appointment.id || appointment._id}
                    className="appointment-card-modern"
                    onClick={() => navigate(`/client/appointment/${publicAppointmentRef(appointment)}`)}
                  >
                    <div className="appointment-card-modern__date">
                      <div className="appointment-card-modern__date-day">{formatAppointmentDate(appointment.dateTime)}</div>
                      <div className="appointment-card-modern__date-time">{formatAppointmentTime(appointment.dateTime)}</div>
                    </div>
                    <div className="appointment-card-modern__main">
                      <div className="appointment-card-modern__header">
                        <div className="appointment-card-modern__avatar-wrapper">
                          <img
                            src={appointment.stylist?.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.stylist?.name || 'Stylist')}&size=56&background=random`}
                            alt={appointment.stylist?.name || 'Stylist'}
                            className="appointment-card-modern__avatar"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (!target.src.includes('ui-avatars.com')) {
                                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.stylist?.name || 'Stylist')}&size=56&background=random`;
                              }
                            }}
                          />
                        </div>
                        <div className="appointment-card-modern__info">
                          <h3 className="appointment-card-modern__stylist-name">
                            {appointment.stylist?.name || 'Stylist'}
                          </h3>
                          <p className="appointment-card-modern__service">{appointment.service?.name || 'Service'}</p>
                          {hasAddons && (
                            <p className="appointment-card-modern__addons">
                              Add-ons ({addons.length}): {addons.map((a) => a.name).filter(Boolean).join(', ') || '—'}
                            </p>
                          )}
                        </div>
                      </div>
                      {appointment.discountSource && appointment.discountSource !== 'none' && (
                        <div style={{ marginTop: '4px' }}>
                          <DiscountBadge
                            discountSource={appointment.discountSource}
                            promotionApplied={appointment.promotionApplied}
                            originalPrice={appointment.service?.originalPrice}
                            finalPrice={appointment.service?.finalPrice}
                            showSavings={true}
                            size="sm"
                          />
                        </div>
                      )}
                      <div className="appointment-card-modern__footer">
                        <span className={`appointment-card-modern__status status--${appointment.status || 'pending'}`}>
                          {appointment.status
                            ? appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)
                            : 'Pending'}
                        </span>
                        <div style={{ textAlign: 'right' }}>
                          {appointment.service?.originalPrice && appointment.service.originalPrice !== servicePrice && (
                            <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.8em', display: 'block' }}>
                              {formatCurrency(appointment.service.originalPrice)}
                            </span>
                          )}
                          <span className="appointment-card-modern__price">
                            {hasAddons ? formatCurrency(totalPrice) : formatCurrency(servicePrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state-modern">
                <div className="empty-state-modern__icon">
                  <CalendarIcon />
                </div>
                <h3 className="empty-state-modern__title">No Upcoming Appointments</h3>
                <p className="empty-state-modern__description">
                  Book your next appointment with your stylist
                </p>
                <button 
                  className="empty-state-modern__button"
                  onClick={() => navigate('/client/browse')}
                >
                  <SearchIcon />
                  Browse Stylists
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Nearby Stylists (based on client city/location) */}
        <section className="section-modern" aria-labelledby="nearby-stylists-heading">
          <div className="section-modern__header">
            <h2 id="nearby-stylists-heading" className="section-modern__title">
              {hasClientLocation
                ? `Stylists near ${user?.location?.city}, ${user?.location?.state}`
                : 'Nearby Stylists'}
            </h2>
            {(nearbyStylists.length > 0 || nearbyStylistsLoading) && (
              <button
                type="button"
                className="section-modern__action"
                onClick={() => navigate('/client/browse')}
                aria-label="View all stylists in browse"
              >
                View All
                <ArrowRightIcon />
              </button>
            )}
          </div>
          <div className="section-modern__content">
            {nearbyStylistsLoading ? (
              <div className="client-dashboard-modern__nearby-skeletons">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="appointment-card-modern skeleton" style={{ minHeight: 180 }} />
                ))}
              </div>
            ) : !hasClientLocation ? (
              <div className="empty-state-modern">
                <p className="empty-state-modern__description">
                  Add your city in My Profile to see stylists near you.
                </p>
                <button
                  type="button"
                  className="empty-state-modern__button"
                  onClick={() => navigate('/client/profile')}
                >
                  <PersonIcon />
                  My Profile
                </button>
                <button
                  type="button"
                  className="empty-state-modern__button empty-state-modern__button--secondary"
                  onClick={() => navigate('/client/browse')}
                >
                  <SearchIcon />
                  Browse All Stylists
                </button>
              </div>
            ) : nearbyStylists.length > 0 ? (
              <div className="client-dashboard-modern__nearby-grid">
                {nearbyStylists.map((stylist) => {
                  const withDistance = stylist as Stylist & { distanceFormatted?: string };
                  return (
                    <StylistCard
                      key={stylist._id}
                      stylist={stylist}
                      isFavorite={false}
                      isNearby={Boolean(withDistance.distanceFormatted)}
                      distanceLabel={withDistance.distanceFormatted ?? null}
                      onFavoriteToggle={async () => {}}
                      onClick={() => navigate(`/client/stylist/${publicStylistRef(stylist)}`)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="empty-state-modern">
                <p className="empty-state-modern__description">
                  No stylists found in your area. Try browsing all stylists.
                </p>
                <button
                  type="button"
                  className="empty-state-modern__button"
                  onClick={() => navigate('/client/browse')}
                >
                  <SearchIcon />
                  Browse Stylists
                </button>
              </div>
            )}
          </div>
        </section>
        </div>

        {/* Recent Activity Sidebar */}
        <div className="sidebar-modern">
          {/* Past Appointments */}
          {pastAppointments.length > 0 && (
            <div className="sidebar-section-modern">
              <h3 className="sidebar-section-modern__title">Recent Appointments</h3>
              <div className="sidebar-section-modern__content">
                {pastAppointments.map((appointment) => {
                  const addons = appointment.addons ?? [];
                  const hasAddons = addons.length > 0;
                  return (
                    <div
                      key={appointment.id || appointment._id}
                      className="mini-appointment-modern"
                      onClick={() => navigate(`/client/appointment/${publicAppointmentRef(appointment)}`)}
                    >
                      <div className="mini-appointment-modern__avatar">
                        <img
                          src={appointment.stylist?.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.stylist?.name || 'Stylist')}&size=40&background=random`}
                          alt={appointment.stylist?.name || 'Stylist'}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (!target.src.includes('ui-avatars.com')) {
                              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.stylist?.name || 'Stylist')}&size=40&background=random`;
                            }
                          }}
                        />
                      </div>
                      <div className="mini-appointment-modern__info">
                        <div className="mini-appointment-modern__name">{appointment.stylist?.name || 'Stylist'}</div>
                        <div className="mini-appointment-modern__service">{appointment.service?.name || 'Service'}</div>
                        {hasAddons && (
                          <div className="mini-appointment-modern__addons">Add-ons ({addons.length})</div>
                        )}
                        <div className="mini-appointment-modern__date">
                          {formatAppointmentDate(appointment.dateTime)}
                        </div>
                      </div>
                      <div className={`mini-appointment-modern__status status--${appointment.status || 'pending'}`}>
                        {appointment.status === 'completed' ? '✓' : '●'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Notifications */}
          {notifications.length > 0 && (
            <div className="sidebar-section-modern">
              <div className="sidebar-section-modern__header">
                <h3 className="sidebar-section-modern__title">Notifications</h3>
                <button
                  className="sidebar-section-modern__action"
                  onClick={() => navigate('/client/notifications')}
                >
                  View All
                </button>
              </div>
              <div className="sidebar-section-modern__content">
                {notifications.slice(0, 3).map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item-modern ${notification.read ? 'read' : 'unread'}`}
                  >
                    <div className="notification-item-modern__icon">
                      {notification.type === 'appointment_reminder' && '🔔'}
                      {notification.type === 'loyalty_points' && '⭐'}
                      {notification.type === 'promotion' && '🎁'}
                    </div>
                    <div className="notification-item-modern__content">
                      <p className="notification-item-modern__message">{notification.message}</p>
                      <span className="notification-item-modern__date">
                        {notification.date && !isNaN(new Date(notification.date).getTime())
                          ? format(new Date(notification.date), 'MMM d')
                          : 'Recently'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="sidebar-section-modern">
            <h3 className="sidebar-section-modern__title">Quick Actions</h3>
            <div className="quick-actions-modern">
              <button
                className="quick-action-modern"
                onClick={() => navigate('/client/browse')}
              >
                <div className="quick-action-modern__icon">
                  <SearchIcon />
                </div>
                <span>Browse Stylists</span>
              </button>
              <button
                className="quick-action-modern"
                onClick={() => navigate('/client/appointments')}
              >
                <div className="quick-action-modern__icon">
                  <CalendarIcon />
                </div>
                <span>My Appointments</span>
              </button>
              <button
                className="quick-action-modern"
                onClick={() => navigate('/client/profile')}
              >
                <div className="quick-action-modern__icon">
                  <PersonIcon />
                </div>
                <span>My Profile</span>
              </button>
              {pastAppointments.length > 0 && (
                <button
                  className="quick-action-modern"
                  onClick={() => navigate(`/client/appointment/${publicAppointmentRef(pastAppointments[0])}`)}
                >
                  <div className="quick-action-modern__icon">
                    <StarIcon />
                  </div>
                  <span>Leave a Review</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardPage;
